import type { DateTime } from 'luxon'

/**
 * Row state — the four visual states an individual step row can be in.
 *
 * - `past` — PTU end-time has already elapsed
 * - `now` — PTU contains the current wall-clock moment
 * - `activated` — this specific step has been activated (per-row, not per-PTU)
 * - `under-offered` — PTU's total offered volume < OBL (per-PTU, applied to every row in the PTU)
 */
export type BidDetailRowState = Readonly<{
  past: boolean
  now: boolean
  activated: boolean
  underOffered: boolean
}>

export type BidDetailRowStateInputs = Readonly<{
  ptuStart: DateTime
  ptuEnd: DateTime
  /** Wall-clock reference — caller supplies; usually `DateTime.now().setZone(MARKET_TIMEZONE)`. */
  now: DateTime
  /** Step-level — true when this specific step was activated. */
  activated: boolean
  /** PTU-level — true when offered (across all AGs) < OBL. */
  underOffered: boolean
}>

/**
 * Pure derivation of the row state from the row's PTU bounds + wall-clock +
 * per-row/per-PTU flags. Pulled out of the renderer for testability — the
 * MUI `sx` mapping lives separately in `getRowStateSx`.
 */
export function getRowStateClasses(inputs: BidDetailRowStateInputs): BidDetailRowState {
  const { ptuStart, ptuEnd, now, activated, underOffered } = inputs

  const past = ptuEnd <= now
  const isNow = !past && ptuStart <= now && now < ptuEnd

  return {
    past,
    now: isNow,
    activated,
    underOffered,
  }
}
